import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UNSAFE_RouteContext } from 'react-router-dom';

/**
 * Route metadata is static: a route knows it is "a source page", never that it is
 * "Acme Webhook". The normal react-router answer is a route loader feeding the
 * handle, but this app has no loaders — data lives in MobX stores and component
 * effects — so the page publishes its own name instead and one component up in
 * the layout composes the title from it.
 *
 * Pages must only ever publish. Keeping the single `document.title` write in the
 * title host is what stops a page that sets a title from leaving it stale when it
 * unmounts into a page that does not.
 *
 * Publishers are held in a registry rather than a single slot because a page
 * routinely has more than one: the shared TabPageLayout and, nested inside it,
 * the shared Navigation header. React runs child effects before parent effects,
 * so with a single slot the outer layout's "no name" would land last and blank
 * the real name the inner header had just published. The registry ignores
 * publishers that have no name to offer, so the two compose instead of racing.
 */

type PublisherId = symbol;

/** A published name plus how deep in the route tree it was published from. */
type Publication = { name?: string; depth: number };

type RouteEntityNameContextValue = {
  entityName?: string;
  publishEntityName: (id: PublisherId, publication: Publication) => void;
  withdrawEntityName: (id: PublisherId) => void;
};

const noop = (): void => undefined;

const RouteEntityNameContext = createContext<RouteEntityNameContextValue>({
  publishEntityName: noop,
  withdrawEntityName: noop,
});

/**
 * The name published from deepest in the route tree.
 *
 * Ranked by route depth rather than registration order. Effect order alone is
 * not enough: a RouteBoundary sits between a section layout and its lazy page,
 * so on a cold chunk the layout commits by itself and registers first, while on
 * a warm chunk both commit together — the same URL would otherwise resolve to a
 * different title depending on how the user arrived. Depth makes the page's own
 * header outrank the shared layout wrapping it, by construction.
 *
 * Ties break towards the later publisher, so a page can still override a shared
 * component rendered at the same depth.
 */
const deepestPublishedName = (publishers: Map<PublisherId, Publication>) => {
  let deepest: Publication | undefined;

  publishers.forEach((publication) => {
    if (publication.name?.trim() && (!deepest || publication.depth >= deepest.depth)) {
      deepest = publication;
    }
  });

  return deepest?.name;
};

export const RouteEntityNameProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const publishers = useRef(new Map<PublisherId, Publication>());
  const [entityName, setEntityName] = useState<string | undefined>();

  const publishEntityName = useCallback((id: PublisherId, publication: Publication) => {
    publishers.current.set(id, publication);
    setEntityName(deepestPublishedName(publishers.current));
  }, []);

  const withdrawEntityName = useCallback((id: PublisherId) => {
    publishers.current.delete(id);
    setEntityName(deepestPublishedName(publishers.current));
  }, []);

  const value = useMemo(
    () => ({ entityName, publishEntityName, withdrawEntityName }),
    [entityName, publishEntityName, withdrawEntityName],
  );

  return (
    <RouteEntityNameContext.Provider value={value}>{children}</RouteEntityNameContext.Provider>
  );
};

/** Read by the title and Page Context hosts. */
export const useRouteEntityName = (): string | undefined =>
  useContext(RouteEntityNameContext).entityName;

/**
 * Publishes the name of the entity the current page is about, so the tab title
 * can read `Acme Webhook - Sources`.
 *
 * Safe to call with `undefined` — a shared layout that was given no name simply
 * offers nothing, and does not blank a name published elsewhere on the page. The
 * title falls back to the section on its own and updates in place once a name
 * arrives.
 */
export const usePublishRouteEntityName = (entityName?: string): void => {
  const { publishEntityName, withdrawEntityName } = useContext(RouteEntityNameContext);
  // Read straight off the route context, which defaults to an empty match list
  // outside a router rather than throwing the way useMatches() would.
  const { matches } = useContext(UNSAFE_RouteContext);
  const depth = matches.length;
  const idRef = useRef<PublisherId>();

  if (!idRef.current) {
    idRef.current = Symbol('routeEntityNamePublisher');
  }

  const id = idRef.current;

  useEffect(() => {
    publishEntityName(id, { name: entityName, depth });
  }, [id, entityName, depth, publishEntityName]);

  // Withdraw on unmount only. Keyed to the name instead, this would drop the
  // entry before every change and flash the bare section title in between.
  useEffect(
    () => () => {
      withdrawEntityName(id);
    },
    [id, withdrawEntityName],
  );
};
